import {
  type SpeakerBindingKey,
  type SpeakerControlConfiguration,
} from '@src/components/sidepanel/CompositionSettings/types';
import {
  Button,
  CaretDownIcon,
  CaretUpIcon,
  Dropdown,
  DropdownMenu,
  DropdownOption,
} from 'datocms-react-ui';

type SpeakerControlSelectorProps = {
  control: SpeakerControlConfiguration & { enabled?: boolean };
  onUpdateSpeaker: (id: string, type: SpeakerBindingKey) => void;
  options: { id: string; label: string }[];
  headshotOptions: { id: string; label: string }[];
};

/**
 * The speaker's text fields a nametag's text controls can be bound to
 * (PLAT-12179): the two lines, Name and Title — typed, or derived in the Brand
 * Kit's Speaker Format — and the facts on their own elements. A two-line
 * nametag binds Name and Title; a three-line one Name, Position and Company,
 * in whichever order its elements are. A component without one leaves it at
 * None.
 */
const TEXT_FIELDS: Array<{ key: SpeakerBindingKey; label: string }> = [
  { key: 'name', label: 'Name' },
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'title', label: 'Title' },
  { key: 'role', label: 'Position' },
  { key: 'organization', label: 'Company' },
];

export function SpeakerControlSelector({
  control,
  onUpdateSpeaker,
  options,
  headshotOptions,
}: SpeakerControlSelectorProps) {
  // Which option is currently bound to each speaker field. Phase 2's
  // `roleLine` binding is the Title line by its old name (PLAT-12176).
  const boundOptionId = (key: SpeakerBindingKey) =>
    Object.entries(control.controlBindings || {}).find(
      ([, value]) => value === key || (key === 'title' && value === 'roleLine'),
    )?.[0];

  const renderDropdown = (
    key: SpeakerBindingKey,
    label: string,
    fieldOptions: { id: string; label: string }[],
    // Text options already bound to ANOTHER text field; one text control can
    // show only one speaker field.
    takenIds: Set<string>,
  ) => {
    const activeId = boundOptionId(key);
    const activeLabel =
      fieldOptions.find(o => o.id === activeId)?.label || 'None';
    return (
      <Dropdown
        key={key}
        renderTrigger={({ open, onClick }) => (
          <Button
            buttonSize="xxs"
            type="button"
            onClick={onClick}
            rightIcon={open ? <CaretUpIcon /> : <CaretDownIcon />}
          >
            <span
              style={{ textTransform: 'capitalize' }}
            >{`${label}: ${activeLabel}`}</span>
          </Button>
        )}
      >
        <DropdownMenu>
          {fieldOptions.map(o => (
            <DropdownOption
              key={o.id}
              disabled={takenIds.has(o.id)}
              active={activeId === o.id}
              onClick={() => {
                onUpdateSpeaker(o.id, key);
              }}
            >
              {o.label}
            </DropdownOption>
          ))}
          <DropdownOption
            active={activeId === 'none'}
            onClick={() => {
              onUpdateSpeaker('none', key);
            }}
          >
            None
          </DropdownOption>
        </DropdownMenu>
      </Dropdown>
    );
  };

  return (
    <>
      {TEXT_FIELDS.map(({ key, label }) => {
        const takenIds = new Set(
          TEXT_FIELDS.filter(other => other.key !== key)
            .map(other => boundOptionId(other.key))
            .filter((id): id is string => !!id && id !== 'none'),
        );
        return renderDropdown(key, label, options, takenIds);
      })}
      {renderDropdown('headshot', 'Headshot', headshotOptions, new Set())}
    </>
  );
}
